<script lang="ts">
	import { beforeNavigate, afterNavigate } from '$app/navigation';
	import Footer from '$lib/components/layout/footer.svelte';
	import Header from '$lib/components/layout/header.svelte';
	import type { Snippet } from 'svelte';
	import Matomo from '$lib/components/tracking/Matomo.svelte';
	import { page } from '$app/stores';
	import StructuredData from '$lib/components/layout/structured-data.svelte';
	
	import '../app.css';
	import { SvelteMap } from 'svelte/reactivity';
	import { dev } from '$app/environment';

	let { children }: { children: Snippet } = $props();

	let has_navigated = false;

	function keep_last_only(selector: string) {
		const elements = Array.from(document.querySelectorAll(`head ${selector}`));
		if (elements.length <= 1) return;
		elements.slice(0, -1).forEach((el) => el.remove());
	}

	function deduplicate_by_attribute(selector: string, attr: string) {
		const groups = new SvelteMap<string, Element[]>();
		document.querySelectorAll(`head ${selector}`).forEach((el) => {
			const key = el.getAttribute(attr);
			if (key) {
				if (!groups.has(key)) groups.set(key, []);
				groups.get(key)!.push(el);
			}
		});
		groups.forEach((elements) => {
			if (elements.length <= 1) return;
			const defaultOgImage = '/talentprofile-homepage-og.png';
			const preferred = elements.find((el) => {
				const content = el.getAttribute('content') || '';
				return content.length > 0 && !content.endsWith(defaultOgImage);
			});
			const keeper = preferred || elements[elements.length - 1];
			elements.forEach((el) => {
				if (el !== keeper) el.remove();
			});
		});
	}

	function deduplicateHead() {
		if (typeof document === 'undefined') return;
		['title', 'link[rel="canonical"]', 'link[rel="amphtml"]', 'base[href]'].forEach(keep_last_only);
		deduplicate_by_attribute('meta[name]', 'name');
		deduplicate_by_attribute('meta[property]', 'property');
		deduplicate_by_attribute('link[rel="alternate"][hreflang]', 'hreflang');
		keep_last_only('script[type="application/ld+json"]');
	}

	beforeNavigate(() => {
		if (has_navigated) deduplicateHead();
	});
	afterNavigate(() => {
		if (has_navigated) {
			deduplicateHead();
		} else {
			has_navigated = true;
		}
	});
</script>

<Header />

<!-- <svelte:boundary onerror={console.error}>{@render children()}</svelte:boundary> -->
{@render children()}

<Footer />

<svg width="0" height="0">
	<clipPath id="superellipse" clipPathUnits="objectBoundingBox">
		<path
			d="M0.02879 0.16957 C0.04211 0.09740 0.09772 0.04316 0.16991 0.02998 C0.24977 0.01540 0.36585 0 0.5 0 C0.63447 0 0.75079 0.01547 0.83066 0.03009 C0.90253 0.04323 0.95796 0.09711 0.97126 0.16894 C0.98546 0.24567 1 0.35872 1 0.5 C1 0.64459 0.98477 0.75962 0.97026 0.83639 C0.95726 0.90518 0.90524 0.95741 0.83645 0.97043 C0.76007 0.98489 0.64545 1 0.5 1 C0.35491 1 0.24050 0.98496 0.16412 0.97054 C0.09500 0.95749 0.04281 0.90490 0.02979 0.83577 C0.01526 0.75865 0 0.64348 0 0.5 C0 0.35980 0.01457 0.24663 0.02879 0.16957 Z"
		/>
	</clipPath>
</svg>

{#if !dev}
	<Matomo />
{/if}

{#if $page.data?.seo?.structuredData?.length}
	<StructuredData schemas={$page.data.seo.structuredData} />
{/if}
<svelte:head>
	<link rel="preconnect" href="https://api.talentprofile.net" crossorigin="anonymous" />
	<script>
		if (!window.requestIdleCallback) {
			window.requestIdleCallback = function (cb, options) {
				var start = Date.now();
				return setTimeout(
					function () {
						cb({
							didTimeout: false,
							timeRemaining: function () {
								return Math.max(0, 50 - (Date.now() - start));
							}
						});
					},
					options && options.timeout ? options.timeout : 1
				);
			};
			window.cancelIdleCallback = function (id) {
				clearTimeout(id);
			};
		}
	</script>
</svelte:head>
